import * as React from "react";
import { CalendarDays } from "lucide-react";
import { fmtIso } from "@/lib/format";
import { cn } from "@/lib/utils";

export const inputClass =
  "flex h-11 w-full rounded-xl border border-input bg-card px-3.5 text-base shadow-sm lg:text-[0.95rem] transition-colors placeholder:text-muted-foreground/70 hover:border-brand-200 focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/25 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/20";

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, type, ...props }, ref) =>
  type === "date" ? (
    <DateInput className={className} ref={ref} {...props} />
  ) : (
    <input type={type} className={cn(inputClass, "file:border-0 file:bg-transparent file:text-sm file:font-medium", className)} ref={ref} {...props} />
  ),
);
Input.displayName = "Input";

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
  <textarea className={cn(inputClass, "min-h-[96px] py-2.5", className)} ref={ref} {...props} />
));
Textarea.displayName = "Textarea";

/** Natív select a saját stílusunkkal (mobilon a rendszer választóját kapjuk, ami ott a legkényelmesebb). */
export const NativeSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({ className, children, ...props }, ref) => (
  <div className="relative min-w-0">
    <select ref={ref} className={cn(inputClass, "min-w-0 max-w-full appearance-none truncate pr-10", className)} {...props}>
      {children}
    </select>
    <svg className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
  </div>
));
NativeSelect.displayName = "NativeSelect";

/**
 * Dátummező: a látható szöveg mindig magyar formátumú (2026. 09. 21.), a natív mező átlátszóan
 * fölötte van, így koppintásra / kattintásra a böngésző saját naptára nyílik. A natív mező
 * formátuma a böngésző nyelvét és az iOS saját megjelenítését követné (tördelés, eltérő méret).
 */
const DateInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, value, placeholder, disabled, ...props }, ref) => {
  const v = typeof value === "string" ? value : "";
  return (
    <div
      className={cn(
        inputClass,
        "relative items-center gap-2 overflow-hidden whitespace-nowrap focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-ring/25",
        disabled && "cursor-not-allowed opacity-60",
        className,
      )}
    >
      <span className={cn("min-w-0 flex-1 truncate", !v && "text-muted-foreground/70")}>{v ? fmtIso(v) : (placeholder ?? "éééé. hh. nn.")}</span>
      <CalendarDays className="h-4 w-4 shrink-0 opacity-50" />
      <input
        type="date"
        ref={ref}
        value={value}
        disabled={disabled}
        onClick={(e) => {
          try {
            (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
          } catch {
            // Néhány böngésző csak felhasználói gesztusra engedi – ilyenkor a natív viselkedés marad.
          }
        }}
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
        {...props}
      />
    </div>
  );
});
DateInput.displayName = "DateInput";
